import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { CommandResult } from './interfaces';
import { Segment } from '../models/Segment';
import { Paragraph } from '../models/Paragraph';
import { TranscriptEditorChangeType } from '../types';

/**
 * Command for deleting a paragraph from a segment
 * 
 * This command handles:
 * 1. Removing a paragraph from a segment
 * 2. If the segment becomes empty, removing the segment as well
 * 3. Storing the state for undo operations
 */
export class DeleteParagraphCommand extends AbstractTranscriptEditorCommand {
  private segmentId: string;
  private paragraphId: string;
  private userId: string;
  private storedParagraph: any = null;
  private storedSegment: any = null;
  private segmentWasDeleted: boolean = false;

  /**
   * Create a new DeleteParagraphCommand
   * 
   * @param editor Reference to the transcript editor core
   * @param segmentId ID of the segment containing the paragraph
   * @param paragraphId ID of the paragraph to delete
   * @param userId ID of the user performing the operation
   */
  constructor(
    editor: TranscriptEditorCore, 
    segmentId: string, 
    paragraphId: string, 
    userId: string
  ) {
    super(`Delete paragraph ${paragraphId}`, editor);
    this.segmentId = segmentId;
    this.paragraphId = paragraphId;
    this.userId = userId;
    
    // Set metadata for debugging/tracking
    this.setMetadata('operation', 'delete_paragraph');
    this.setMetadata('segmentId', segmentId);
    this.setMetadata('paragraphId', paragraphId);
    this.setMetadata('userId', userId);
  }

  /**
   * Execute the command to delete the paragraph
   * 
   * @returns True if successful, false otherwise
   */
  async execute(): Promise<boolean> {
    try {
      if (!this.editor.getContainer()) return false;
      
      const segment = this.editor.getContainer()?.getSegment(this.segmentId);
      if (!segment) return false;
      
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) return false;
      
      // Store the original paragraph for undo
      this.storedParagraph = {
        id: paragraph.id,
        startTime: paragraph.startTime,
        endTime: paragraph.endTime,
        elements: [...paragraph.elements],
        segmentId: paragraph.segmentId
      };
      
      // Remove the paragraph from the segment
      segment.removeParagraph(this.paragraphId);
      
      // Record the operation
      this.editor.recordOperation({
        type: 'paragraph_delete',
        segmentId: this.segmentId,
        paragraphId: this.paragraphId,
        data: {
          paragraph: this.storedParagraph
        },
        userId: this.userId,
        timestamp: Date.now()
      });
      
      // Notify observers of the change
      this.editor.notifyObservers(TranscriptEditorChangeType.PARAGRAPH_DELETED, {
        segment,
        paragraphId: this.paragraphId,
        paragraph: this.storedParagraph
      });
      
      // Check if segment is now empty
      if (segment.paragraphs.length === 0) {
        // Segment has no more paragraphs, delete it
        this.segmentWasDeleted = true;
        
        // Store for undo
        this.storedSegment = {
          id: segment.id,
          speakerLabel: segment.speakerLabel,
          startTime: segment.startTime,
          endTime: segment.endTime
        };
        
        // Remove the segment
        this.editor.getContainer()?.removeSegment(this.segmentId);
        
        // Notify observers of the segment deletion
        this.editor.notifyObservers(TranscriptEditorChangeType.SEGMENT_DELETED, {
          segmentId: this.segmentId,
          segment: this.storedSegment
        });
      }
      
      return true;
    } catch (error) {
      console.error(`Error in DeleteParagraphCommand.execute:`, error);
      return false;
    }
  }

  /**
   * Undo the paragraph deletion
   * 
   * @returns True if successful, false otherwise
   */
  async undo(): Promise<boolean> {
    try {
      if (!this.editor.getContainer()) return false;
      
      // If the segment was deleted, we need to restore it first
      if (this.segmentWasDeleted && this.storedSegment) {
        // Check if segment still doesn't exist
        if (!this.editor.getContainer()?.getSegment(this.segmentId)) {
          // Create a new segment with the original ID
          const newSegment = new Segment(
            this.segmentId,
            this.storedSegment.speakerLabel,
            this.storedSegment.startTime,
            this.storedSegment.endTime,
            [],
          );
          
          // Add to container
          this.editor.getContainer()?.addSegment(newSegment);
        }
      }
      
      // Get the segment again (either existing or newly created)
      const restoredSegment = this.editor.getContainer()?.getSegment(this.segmentId);
      if (!restoredSegment) return false;
      
      // Recreate the paragraph
      const restoredParagraph = new Paragraph(
        this.storedParagraph.id,
        this.storedParagraph.startTime,
        this.storedParagraph.endTime,
        this.storedParagraph.elements,
        restoredSegment.id
      );
      
      // Add to segment
      restoredSegment.addParagraph(restoredParagraph);
      
      // Notify observers of the undo
      this.editor.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_CHANGED, {
        container: this.editor.getContainer()
      });
      
      return true;
    } catch (error) {
      console.error(`Error in DeleteParagraphCommand.undo:`, error);
      return false;
    }
  }
}